<template>
  <div class="footer-menu-container container">
    <div :class="{ row: isDesktop }">
      <div class="footer-menu-list col-12 col-xl-2">
        <ul>
          <li v-for="(item, index) in footerMenu" :key="index">
            <a v-if="item.external" class="menu-item" target="_blank" :href="item.href">
              {{ item.name }}
            </a>

            <NuxtLink v-else class="menu-item" :to="item.href">
              {{ item.name }}
            </NuxtLink>
          </li>
          <NuxtLink
            v-if="!isDesktop"
            class="menu-item newsletter"
            target="_blank"
            to="/newsletter-signup"
          >
            Sign Up for Our Newsletter
          </NuxtLink>
        </ul>

        <div class="footer-contact-details" aria-labelledby="footer-help-heading">
          <h2 id="footer-help-heading" class="visually-hidden">Contact Information</h2>
          <div class="footer-phone">
            <a :href="`tel:${property.contact_phone}`">{{ property.contact_phone }}</a>
          </div>
          <div class="footer-email">
            <a :href="`mailto:${property.contact_email}`">{{ property.contact_email }}</a>
          </div>
          <PropertySocialLinks :type="property" :has_comment="false" />
        </div>
      </div>

      <div class="newsletter-subscribe col-12 col-xl-6" v-if="isDesktop">
        <nuxt-link to="/newsletter-signup">
          <h2 class="">always be</h2>
          <h3 class="">in the know</h3>
          <h4 class="">subscribe to the highland park village newsletter</h4>
          <div class="newsletter-cta">
            ENTER EMAIL ADDRESS
            <FontAwesomeIcon
              icon="fa-solid fa-chevron-right"
              class="chevron-icon"
            />
          </div>
        </nuxt-link>
      </div>

      <div class="lookbook-image-container col-12 col-xl-4" v-if="lookbookImage?.url && lookbookImage.tags.includes('active')">
        <nuxt-link :to="lookbookImage?.url ? lookbookImage?.url : '/store-directory'" class="lookbook-url">
          <NuxtImg
            class="lookbook-image"
            :src="lookbookImage.image_url + '?auto=format,compress&fm=webp'"
            :alt="
              lookbookImage.image_url_alt_text
                ? lookbookImage.image_url_alt_text
                : lookbookImage.name
            "
            :modifiers="{ format: 'webp' }" 
          />
        </nuxt-link>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, watch } from "vue";
import { useMainStore } from "~/store/index";

const store = useMainStore();
const { isMobile, isDesktop } = useMobileChecker();

// PROPS
const props = defineProps(["footerMenu", "property"]);

const galleries = store.galleryData ? store.galleryData : [];

const lookbookImage = galleries?.find(
  (gallery) => gallery.name === "Footer Lookbook Images"
).gallery_items[0];
</script>

<style lang="scss" scoped>
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.footer-menu-container {
  font-size: 12.5px;
  line-height: 16px;
  letter-spacing: 1.25px;
  width: 100%;

  .footer-menu-list {
    padding-left: 0;
    margin-top: 40px;
    font-family: $font_poppin_light;

    ul {
      padding-left: 0;

      li {
        list-style: none;
        margin: 5px 0;
        text-transform: uppercase;

        a {
          color: $black;
          text-decoration: none;
        }

         @media only screen and (max-width: 768px) {
          margin: 8px 0;
         }
      }

      .newsletter {
        list-style: none;
        margin: 4px 0;
        text-transform: uppercase;
        font-family: $font_poppin_bold;
      }
    }

    .footer-contact-details {
      margin-top: 50px;

      .footer-email {
        margin: 4px 0;
      }

      @media only screen and (max-width: 1024px) {
        margin-top: 30px;
      }
    }
  }

  .newsletter-subscribe {
    margin-top: 40px;

    @media only screen and (max-width: 1200px) {
      max-width: unset;
      padding-left: 0px;
      padding-right: 0px;
    }

    a {
      text-decoration: none;
      color: $black;
    }

    h2 {
      font-family: $font_didot_italic;
      text-transform: uppercase;
      letter-spacing: -2px;
      font-size: 32px;
      line-height: 32px;
    }

    h3 {
      font-family: $font_didot_italic;
      text-transform: uppercase;
      letter-spacing: -5px;
      font-size: 72px;
      line-height: 72px;
    }

    h4 {
      font-family: $font_poppin_light;
      text-transform: uppercase;
      letter-spacing: 2px;
      font-size: 14px;
      line-height: 18px;
    }

    .newsletter-cta {
      border-bottom: 3px solid $black;
      width: 100%;
      max-width: 500px;
      margin: 26px 0 51px 0;
      padding: 0 0 10px 0;
      text-align: right;
      font-family: $font_poppin_bold;

      .chevron-icon {
        height: 0.65em;
        font-family: $font_poppin_bold;
        vertical-align: middle;
      }

      @media only screen and (max-width: 1200px) {
        max-width: unset;
      }
    }
  }

  .lookbook-image-container {
    left: -75px;

    .lookbook-image {
      width: 475px;
    }

    @media only screen and (max-width: 1290px) {
      .lookbook-image {
        width: 455px;
      }
    }

    @media only screen and (max-width: 1200px) {
      border-top: 1px solid $black;
      left: 0;

      .lookbook-image {
        width: unset;
        max-width: 100%;
        height: auto;
        margin: 20px auto;
        padding: 1% 25%;
      }
    }

    @media only screen and (max-width: 580px) {
      padding: 0;

      .lookbook-image {
        width: unset;
        max-width: 100%;
        height: auto;
        margin: 20px auto;
        padding: 0%;
      }
    }
  }

  @media only screen and (max-width: 1290px) {
    .row {
      margin-right: 0px;
      margin-left: 0px;
    }
  }

  @media only screen and (max-width: 1024px) {
    .row {
      margin-right: unset;
      margin-left: unset;
    }

    padding: 0 50px;
  }

  @media only screen and (max-width: 580px) {
    padding: 0 20px;
  }
}
</style>
